import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { BrandLogo } from "@/components/LogoMark";
import { PLATFORM_LINKS } from "@/lib/site";

const MAIN_LINKS = [
  { to: "/insights", label: "Insights", key: "insights" },
  { to: "/about", label: "About", key: "about" },
  { to: "/careers", label: "Careers", key: "careers" },
  { to: "/contact", label: "Contact", key: "contact" },
];

export default function SiteHeader({ active }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#040508]/75 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="site-logo" aria-label="Adviserz home" className="shrink-0">
          <BrandLogo className="h-6 md:h-7" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          <div className="relative group">
            <button
              data-testid="nav-platform"
              className="inline-flex items-center gap-1 text-sm text-zinc-400 group-hover:text-white transition-colors py-3"
            >
              Platform <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <div className="absolute left-0 top-full w-[340px] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="surface-elevated rounded-lg p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]">
                {PLATFORM_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.to.slice(1)}`}
                    className="block px-3 py-2.5 rounded-md hover:bg-white/[0.05] transition-colors"
                  >
                    <div className="text-sm text-white">{l.label}</div>
                    <div className="mt-0.5 text-xs text-zinc-500">{l.blurb}</div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {MAIN_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={`text-sm transition-colors ${active === l.key ? "text-white" : "text-zinc-400 hover:text-white"}`}
            >
              {l.label}
            </Link>
          ))}

          <Link
            to="/early-access"
            data-testid="header-cta"
            className="text-sm px-4 py-2 rounded-md border border-white/15 bg-white/[0.03] backdrop-blur text-white hover:bg-white/[0.08] hover:border-white/25 transition-colors"
          >
            Join Early Access
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle navigation"
          className="lg:hidden w-10 h-10 grid place-items-center rounded-md border border-white/10 text-white"
        >
          {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div data-testid="mobile-menu-panel" className="lg:hidden border-t border-white/[0.06] bg-[#08090B] max-h-[75vh] overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            <div>
              <div className="label-xs mb-3">Platform</div>
              <div className="grid gap-1">
                {PLATFORM_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.to.slice(1)}`}
                    className="py-2 text-sm text-zinc-300 hover:text-white">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="label-xs mb-3">Company</div>
              <div className="grid gap-1">
                {MAIN_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.key}`}
                    className="py-2 text-sm text-zinc-300 hover:text-white">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
            <Link to="/early-access" onClick={() => setOpen(false)} data-testid="mobile-header-cta"
              className="block text-center px-5 py-3 rounded-md brand-gradient text-white font-medium">
              Join Early Access
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
